import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import { AppRoutes } from './routes';
import Layout from './layout/Layout';
import useDocumentTitle from './hooks/useDocumentTitle';
import AuthGuard from './components/authentication/AuthGuard';
import LoadingSpinner from './components/common/LoadingSpinner';
import { RouteAccessProvider } from './contexts/RouteAccessContext';
import { cleanupLogoutListeners, setupGlobalLogoutListeners } from './utils/logoutHandler';
import { useTranslation } from 'react-i18next';

// Single app-wide QueryClient - created once at module scope so it survives
// re-renders (and Fast Refresh) rather than being re-instantiated per mount.
const queryClient = new QueryClient();

// Routes centralized in routes.js; local TPRM lazy loads removed

// Axios configured in src/api/axios.js via central import

// Title updater uses a hook

const TitleUpdater = () => { useDocumentTitle(); return null; };

// Layout extracted to src/layout/Layout.jsx

function App() {
  const { i18n } = useTranslation();
  const [selectedDropdown, setSelectedDropdown] = useState("NIST-CSF 2.0");

  // Setup global logout listeners for immediate cross-tab synchronization
  useEffect(() => {
    setupGlobalLogoutListeners();

    return () => {
      cleanupLogoutListeners();
    };
  }, []);

  // ConditionalRoute moved to routes.js

  return (
    <QueryClientProvider client={queryClient}>
      <RouteAccessProvider>
        <Router>
          <TitleUpdater /> {/* Add TitleUpdater here */}
          <div className="App">
            <AuthGuard>
              <Layout selectedDropdown={selectedDropdown} setSelectedDropdown={setSelectedDropdown}>
                <div className="content">
                  <Suspense fallback={<LoadingSpinner message="Loading TPRM module..." />}>
                    <AppRoutes selectedDropdown={selectedDropdown} />
                  </Suspense>
                </div>
              </Layout>
            </AuthGuard>
          </div>
        </Router>
      </RouteAccessProvider>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick={true}
        rtl={i18n.dir() === 'rtl'}
        pauseOnFocusLoss={true}
        draggable={true}
        pauseOnHover={true}
        theme="colored"
        limit={3}
      />
    </QueryClientProvider>
  );
}

export default App;
